<script>
import Vue from 'vue'
import { MODAL, ACTION } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import Title from '@components/section-title'
import { apiAtendimentoConexao } from '@modules/settings/api/apiAtendimentoConexao'
import {
  QRCODE_MENSAGENS,
  QRCODE_STATUS_DEMORA,
  QRCODE_STATUS_MENSAGEM_DO_BACKEND,
  QRCODE_MAX_ERROS_POLLING,
  QRCODE_MENSAGEM_TECNICA,
  QRCODE_ESPERA_MAX_MS,
  QRCODE_STATUS_VEREDITO_DEFINITIVO,
} from '@modules/settings/constants/Atendimento'

export default {
  name: 'ModalQrcodeConexao',
  components: { Title },
  data() {
    return {
      busModal: new Vue(),
      idConexao: null,
      qrCodeBase64: null,
      loading: false,
      // Mensagem PT-BR do que impediu o pareamento. Enquanto preenchida, o modal
      // mostra o estado de erro com a ação de repetir — em vez de girar sobre
      // uma requisição que já morreu.
      erro: null,
      pollingInterval: null,
      pollingBusy: false,
      pollingErrorCount: 0,
      // Instante em que a espera começou — o teto é do FLUXO, não do tick.
      esperaIniciadaEm: null,
      textos: QRCODE_MENSAGENS,
    }
  },
  computed: {
    /**
     * Estado único da área de conteúdo. Sem isto o template precisaria compor
     * `loading`, `qrCodeBase64`, `erro` e `pollingInterval` em cada `v-if` — e
     * foi essa composição implícita que deixava o spinner aceso durante um
     * request já falho.
     */
    estado() {
      if (this.erro) return 'erro'
      if (this.qrCodeBase64) return 'qrcode'
      if (this.loading || this.pollingInterval) return 'carregando'
      return 'erro'
    },

    mensagemErro() {
      return this.erro || this.textos.falha
    },
  },
  created() {
    GlobalBus.$on(MODAL.ATENDIMENTO_QRCODE_CONEXAO, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.ATENDIMENTO_QRCODE_CONEXAO, this.open)
    this.stopPolling()
  },
  methods: {
    clear() {
      this.stopPolling()
      this.idConexao = null
      this.qrCodeBase64 = null
      this.loading = false
      this.erro = null
      this.esperaIniciadaEm = null
    },

    open({ id_conexao } = {}) {
      this.clear()
      this.idConexao = id_conexao
      this.busModal.$emit('dialog', true)
      this.startFlow(id_conexao)
    },

    async startFlow(id_conexao) {
      // Guard de reentrancia: dois cliques em "Tentar novamente" disparavam
      // dois POST /connect e dois ciclos de polling concorrentes.
      if (this.loading) return
      this.loading = true
      this.esperaIniciadaEm = Date.now()
      try {
        const connected = await this.requestConnection(id_conexao)
        if (!connected) return

        const alreadyConnected = await this.loadInitialQrCode(id_conexao)
        if (alreadyConnected) return
      } catch (e) {
        // `open()` dispara este fluxo sem await: uma exceção que escapasse daqui
        // viraria unhandled rejection e deixaria o modal girando sobre uma
        // promise morta — o sintoma original do incidente.
        console.error(e)
        this.handleFalha({ status: 500 })
        return
      } finally {
        this.loading = false
      }

      this.startPolling(id_conexao)
    },

    async requestConnection(id_conexao) {
      const r = await apiAtendimentoConexao.conectar({ id_conexao })
      if (r && r.status === 200) return true

      this.handleFalha(r)
      return false
    },

    async loadInitialQrCode(id_conexao) {
      const r = await apiAtendimentoConexao.getQrCode({ id_conexao })
      if (!r || r.status !== 200 || !r.value) return false

      if (r.value.status === 'conectado') {
        this.handleConnected()
        return true
      }

      this.qrCodeBase64 = r.value.qrcode_base64 || null
      return false
    },

    /**
     * Traduz o envelope de falha para uma frase que o usuário entende.
     *
     * A string do axios ("Request failed with status code 504") não é mensagem
     * de produto: é jargão de transporte, em inglês, e chegava intacta à tela.
     * Só texto vindo do BODY do backend é exibido como veio.
     */
    mensagemDeFalha(r) {
      const status = (r && r.status) || 0
      if (QRCODE_STATUS_DEMORA.indexOf(status) !== -1) return this.textos.timeout
      const doBackend =
        QRCODE_STATUS_MENSAGEM_DO_BACKEND.indexOf(status) !== -1 && r && r.message
      // Nem todo 400 traz texto de produto: o backend interpola a exceção crua
      // da Evolution na mensagem. Vestígio técnico ⇒ frase própria.
      if (doBackend && !QRCODE_MENSAGEM_TECNICA.test(r.message)) return r.message
      return this.textos.falha
    },

    handleFalha(r) {
      this.stopPolling()
      this.loading = false
      this.erro = this.mensagemDeFalha(r)
    },

    async tentarNovamente() {
      if (this.loading) return
      this.erro = null
      this.qrCodeBase64 = null
      await this.startFlow(this.idConexao)
    },

    /** Estourou o teto de espera sem QR? */
    esperouDemais() {
      if (!this.esperaIniciadaEm) return false
      return Date.now() - this.esperaIniciadaEm >= QRCODE_ESPERA_MAX_MS
    },

    startPolling(id_conexao) {
      // Fecha o ciclo anterior antes de abrir outro: `setInterval` sobrescrito
      // sem `clearInterval` deixa o timer velho vivo, batendo na Evolution a
      // cada 5s ate a saida da pagina — o storm que o guard de in-flight
      // (incidente 5122) existe para evitar.
      this.stopPolling()
      this.pollingErrorCount = 0
      this.pollingBusy = false
      this.pollingInterval = setInterval(() => {
        this.pollQrCodeTick(id_conexao)
      }, 5000)
    },

    async pollQrCodeTick(id_conexao) {
      // O teto é avaliado AQUI, antes do guard de in-flight, porque o tick roda
      // a cada 5s independentemente do request pendurado. Medido na stack local:
      // avaliando só na volta do request (que leva ~24s quando a Evolution não
      // responde), o guard de in-flight pula ticks e o corte de 90s só era
      // testado a cada ~24s — em 100s o modal seguia girando.
      if (!this.qrCodeBase64 && this.esperouDemais()) {
        this.handleFalha({ status: 408 })
        return
      }

      // Guard de in-flight: se o tick anterior ainda não respondeu (backend
      // lento), pula este tick em vez de empilhar requests presos — cada um
      // seguraria um worker do backend (amplificador do storm, incidente 5122).
      if (this.pollingBusy) return
      this.pollingBusy = true
      try {
        const r = await apiAtendimentoConexao.getQrCode({ id_conexao })
        this.applyQrCodePollResult(r)
      } finally {
        this.pollingBusy = false
      }
    },

    applyQrCodePollResult(r) {
      if (r && r.status === 200 && r.value) {
        this.pollingErrorCount = 0
        if (r.value.status === 'conectado') {
          this.handleConnected()
          return
        }
        this.qrCodeBase64 = r.value.qrcode_base64 || this.qrCodeBase64
        return
      }
      // Veredito de negócio não melhora com insistência: mostra na primeira.
      // O contador é para falha transitória de transporte.
      const status = (r && r.status) || 0
      if (QRCODE_STATUS_VEREDITO_DEFINITIVO.indexOf(status) !== -1) {
        this.handleFalha(r)
        return
      }

      this.pollingErrorCount++
      if (this.pollingErrorCount >= QRCODE_MAX_ERROS_POLLING) {
        // Desistir fechando o modal tirava o contexto de quem estava esperando.
        // O erro fica onde o usuário já está olhando, com a saída ao lado.
        this.handleFalha(r)
      }
    },

    stopPolling() {
      this.pollingBusy = false
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    handleConnected() {
      this.stopPolling()
      this.busModal.$emit('dialog', false)
      this.$showSnack({
        text: 'WhatsApp conectado com sucesso!',
        button: true,
      })
      GlobalBus.$emit(ACTION.ATENDIMENTO_CONEXAO_CONECTADA, {
        id_conexao: this.idConexao,
      })
    },

    onClose() {
      this.stopPolling()
    },
  },
}
</script>

<template>
  <BaseModal
    centerScreen
    :bus="busModal"
    persistent
    centerAndDecreasesPadding
    maxWidth="480px"
    closeColor="#8e8e8e"
    backgroundColor="white"
    @close="onClose"
  >
    <template v-slot:title>
      <Title class="mb-4" title="Conectar WhatsApp" underlined />
    </template>

    <div class="pb-6">
      <div class="modal-content text-center">
        <p
          v-if="estado !== 'erro'"
          class="az-text-primary az-size-14 az-muted mb-6"
        >
          Abra o WhatsApp no seu celular, vá em
          <strong>
            Configurações > Aparelhos conectados > Conectar um aparelho
          </strong>
          e escaneie o QR Code abaixo.
        </p>

        <v-fade-transition mode="out-in">
          <div
            v-if="estado === 'carregando'"
            key="carregando"
            class="d-flex flex-column justify-center align-center py-8"
          >
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="az-text-primary az-size-14 az-muted mt-4 mb-1">
              {{ textos.gerando }}
            </p>
            <p class="az-text-primary az-size-12 az-muted mb-0">
              {{ textos.demora }}
            </p>
          </div>

          <div
            v-else-if="estado === 'qrcode'"
            key="qrcode"
            class="d-flex justify-center align-center py-2"
          >
            <img
              :src="qrCodeBase64"
              alt="QR Code WhatsApp"
              class="qrcode-image"
            />
          </div>

          <div
            v-else
            key="erro"
            class="d-flex flex-column justify-center align-center py-8"
          >
            <icon-svg name="error-circle" :size="40" />
            <p class="az-text-primary az-size-14 az-muted mt-4 mb-4 erro-texto">
              {{ mensagemErro }}
            </p>
            <BaseButton primary @click="tentarNovamente">
              {{ textos.tentarNovamente }}
            </BaseButton>
          </div>
        </v-fade-transition>
      </div>
    </div>

    <template v-slot:actions="{ close }">
      <div class="mt-4 d-flex">
        <BaseButton underlined @click="close"> Cancelar </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<style lang="sass" scoped>
.qrcode-image
  max-width: 280px
  max-height: 280px

.erro-texto
  max-width: 340px
</style>
